import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { 
  Box, 
  Button, 
  CircularProgress, 
  Divider,
  FormControl,
  Grid, 
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField, 
  Typography 
} from '@mui/material';
import { RootState, useAppDispatch, useAppSelector } from "../../../../state/state.tsx";
import { closeModal, toggleLoading } from "../../../../state/ModalSlice/ModalSlice.ts";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from 'react-toastify';
import { db } from "../../../../Config/firebase.ts";
import { doc, updateDoc, arrayUnion, getDoc } from "firebase/firestore";
import HistoryIcon from '@mui/icons-material/History';
import { selectEffectiveUID } from '../../../../state/UserStatesV2/userSliceV2.ts';   
interface Lead {
  id: string;
  name: string;
  phone: string;
  email: string;
  source: string;
  status: string;
  notes: string;
  lastContact: string;
  originatorId?: string;
  notesHistory?: Array<{
    timestamp: number;
    note: string;
    updatedBy?: string;
  }>;
  updateHistory?: Array<{
    timestamp: number;
    updatedBy?: string;
    changes: Record<string, any>;
  }>;
}

type EditLeadProps = {
  lead: Lead | null;
  updateLead?: (lead: Lead) => void;
};

export default function EditLeadModal({ lead, updateLead }: EditLeadProps) {
  const open = useAppSelector((state: RootState) => state.ModalSlice.EditLeadModalOpen);
  const isLoading = useAppSelector((state: RootState) => state.ModalSlice.isLoading);
  const userId = useAppSelector(selectEffectiveUID); 
  const userEmail = useAppSelector((state: RootState) => state.userSliceV2.data?.email);
  const userDisplayName = useAppSelector((state: RootState) => state.userSliceV2.data?.display_name);
  const dispatch = useAppDispatch();

  const [formData, setFormData] = useState<Lead>({
    id: '',
    name: '',
    phone: '',
    email: '',
    source: 'Website',
    status: 'hot',
    notes: '',
    lastContact: new Date().toISOString().split('T')[0],
    originatorId: '',
    notesHistory: [],
    updateHistory: []
  });

  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (lead) {
      setFormData({
        ...lead,
        notesHistory: lead.notesHistory || [],
        updateHistory: lead.updateHistory || []
      });
      
      // If lead doesn't have notes history, fetch it from Firestore
      if (!lead.notesHistory && userId && lead.id) {
        fetchLeadDetails(lead.id);
      }
    }
  }, [lead, userId]);

  const fetchLeadDetails = async (leadId: string) => {
    try {
      const leadDoc = await getDoc(doc(db, `ServiceProviders/${userId}/Leads`, leadId));
      if (leadDoc.exists()) {
        const leadData = leadDoc.data() as Lead;
        setFormData(prevData => ({
          ...prevData,
          notesHistory: leadData.notesHistory || [],
          updateHistory: leadData.updateHistory || []
        }));
      }
    } catch (error) {
      console.error("Error fetching lead details:", error);
    }
  };

  const handleClose = () => {
    dispatch(closeModal("EditLeadModalOpen"));
    setShowHistory(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({...formData, [e.target.name]: e.target.value});
  };

  const handleSelectChange = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const name = e.target.name as string;
    const value = e.target.value as string;
    setFormData({...formData, [name]: value});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate phone number format (10 digits)
    if (!/^\d{10}$/.test(formData.phone)) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (!userId || !lead?.id) {
      toast.error("Missing user or lead information");
      return;
    }

    try {
      dispatch(toggleLoading(true));
      
      const timestamp = Date.now();
      const updatedBy = userDisplayName || userEmail || 'Unknown';
      
      // Track changes
      const changes: Record<string, any> = {};
      
      if (lead.name !== formData.name) changes.name = { from: lead.name, to: formData.name };
      if (lead.phone !== formData.phone) changes.phone = { from: lead.phone, to: formData.phone };
      if (lead.email !== formData.email) changes.email = { from: lead.email, to: formData.email };
      if (lead.source !== formData.source) changes.source = { from: lead.source, to: formData.source };
      if (lead.status !== formData.status) changes.status = { from: lead.status, to: formData.status };
      if (lead.lastContact !== formData.lastContact) changes.lastContact = { from: lead.lastContact, to: formData.lastContact };
      if (lead.originatorId !== formData.originatorId) changes.originatorId = { from: lead.originatorId, to: formData.originatorId };
      
      // Create notes history entry if notes changed
      let notesHistoryEntry = null;
      if (lead.notes !== formData.notes) {
        notesHistoryEntry = {
          timestamp,
          note: lead.notes,  // Store the previous note
          updatedBy
        };
      }
      
      // Create update history entry
      const updateHistoryEntry = {
        timestamp,
        updatedBy,
        changes
      };
      
      // Update lead in Firestore
      const leadDocRef = doc(db, `ServiceProviders/${userId}/Leads`, lead.id);
      
      const updateData: any = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        source: formData.source,
        status: formData.status,
        notes: formData.notes,
        lastContact: formData.lastContact,
        originatorId: formData.originatorId,
        updatedAt: timestamp
      };
      
      // Add notes to history if changed
      if (notesHistoryEntry) {
        updateData.notesHistory = arrayUnion(notesHistoryEntry);
      }
      
      // Add to update history if there are changes
      if (Object.keys(changes).length > 0) {
        updateData.updateHistory = arrayUnion(updateHistoryEntry);
      }
      
      await updateDoc(leadDocRef, updateData);
      
      // Update local state for UI
      const updatedFormData = {
        ...formData,
        notesHistory: [
          ...(formData.notesHistory || []),
          ...(notesHistoryEntry ? [notesHistoryEntry] : [])
        ],
        updateHistory: [
          ...(formData.updateHistory || []),
          ...(Object.keys(changes).length > 0 ? [updateHistoryEntry] : [])
        ]
      };
      
      if (updateLead) {
        updateLead(updatedFormData);
      }
      
      toast.success("Lead updated successfully!");
      handleClose();
    } catch (error) {
      console.error("Error updating lead:", error);
      toast.error("Failed to update lead. Please try again.");
    } finally {
      dispatch(toggleLoading(false));
    }
  };

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  if (!lead) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '10px',
        },
      }}
    >
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          p: 2,
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
        }}
      >
        <Typography variant="h6">Edit Lead</Typography>
        <Box>
          <Button 
            startIcon={<HistoryIcon />} 
            onClick={() => setShowHistory(!showHistory)}
            sx={{ mr: 1 }}
          >
            {showHistory ? 'Hide History' : 'Show History'}
          </Button>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
      
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={showHistory ? 7 : 12}>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="Full Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Phone Number"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="10-digit number"
                    disabled={isLoading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required disabled={isLoading}>
                    <InputLabel id="lead-source-label">Source</InputLabel>
                    <Select
                      labelId="lead-source-label"
                      id="lead-source"
                      name="source"
                      value={formData.source}
                      label="Source"
                      onChange={handleSelectChange as any}
                    >
                      <MenuItem value="Website">Website</MenuItem>
                      <MenuItem value="Referral">Referral</MenuItem>
                      <MenuItem value="Social Media">Social Media</MenuItem>
                      <MenuItem value="Email Campaign">Email Campaign</MenuItem>
                      <MenuItem value="Trade Show">Trade Show</MenuItem>
                      <MenuItem value="Cold Call">Cold Call</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required disabled={isLoading}>
                    <InputLabel id="lead-status-label">Status</InputLabel>
                    <Select
                      labelId="lead-status-label"
                      id="lead-status"
                      name="status"
                      value={formData.status}
                      label="Status"
                      onChange={handleSelectChange as any}
                    >
                      <MenuItem value="hot">Hot</MenuItem>
                      <MenuItem value="cold">Cold</MenuItem>
                      <MenuItem value="obsolete">Obsolete</MenuItem>
                      <MenuItem value="win">Win</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    type="date"
                    label="Last Contact"
                    name="lastContact"
                    value={formData.lastContact}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    disabled={isLoading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Originator ID"
                    name="originatorId"
                    value={formData.originatorId || ''}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    disabled={isLoading}
                  />
                </Grid>
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={isLoading}
                    startIcon={isLoading ? <CircularProgress size={24} color="inherit" /> : null}
                  >
                    {isLoading ? 'Updating...' : 'Update Lead'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          
          {showHistory && (
            <Grid item xs={12} md={5}>
              <Paper sx={{ p: 2, maxHeight: '600px', overflow: 'auto' }}>
                <Typography variant="h6" gutterBottom>
                  Edit History
                </Typography>
                
                {formData.updateHistory && formData.updateHistory.length > 0 ? (
                  formData.updateHistory.sort((a, b) => b.timestamp - a.timestamp).map((update, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="primary">
                        {formatTimestamp(update.timestamp)} by {update.updatedBy || 'Unknown'}
                      </Typography>
                      <Box sx={{ ml: 2 }}>
                        {Object.entries(update.changes).map(([field, change]: [string, any]) => (
                          <Typography key={field} variant="body2">
                            Changed {field}: from "{change.from}" to "{change.to}"
                          </Typography>
                        ))}
                      </Box>
                      <Divider sx={{ my: 1 }} />
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No edit history available
                  </Typography>
                )}
                
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  Notes History
                </Typography>
                
                {formData.notesHistory && formData.notesHistory.length > 0 ? (
                  formData.notesHistory.sort((a, b) => b.timestamp - a.timestamp).map((noteEntry, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="primary">
                        {formatTimestamp(noteEntry.timestamp)} by {noteEntry.updatedBy || 'Unknown'}
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 1, mt: 1, bgcolor: 'background.default' }}>
                        <Typography variant="body2" whiteSpace="pre-wrap">
                          {noteEntry.note}
                        </Typography>
                      </Paper>
                      <Divider sx={{ my: 1 }} />
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No notes history available
                  </Typography>
                )}
              </Paper>
            </Grid>
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  );
} 